import {isDisplayableEntityLabel} from '../services/inlineComponents/decodeInlineComponentStream';

/**
 * @param {string} entityIdA
 * @param {string} entityIdB
 * @param {Array<{ from: string, to: string }>} connections
 * @returns {boolean}
 */
export function areDirectlyConnected(entityIdA, entityIdB, connections = []) {
    if (!entityIdA || !entityIdB || entityIdA === entityIdB) {
        return false;
    }
    return connections.some(({from, to}) => (
        (from === entityIdA && to === entityIdB)
        || (from === entityIdB && to === entityIdA)
    ));
}

/**
 * Entities on the chart that share no direct arrow edge.
 *
 * @param {Array<{ wikidataId: string }>} chartEntities
 * @param {Array<{ from: string, to: string }>} chartConnections
 * @returns {Array<{ from: string, to: string }>}
 */
export function listDisjointEntityPairs(chartEntities = [], chartConnections = []) {
    const ids = chartEntities.map((entity) => entity.wikidataId).filter(Boolean);
    const pairs = [];

    for (let i = 0; i < ids.length; i += 1) {
        for (let j = i + 1; j < ids.length; j += 1) {
            const from = ids[i];
            const to = ids[j];
            if (!areDirectlyConnected(from, to, chartConnections)) {
                pairs.push({from, to});
            }
        }
    }

    return pairs;
}

/**
 * @param {Array<{ wikidataId: string, label?: string }>} chartEntities
 * @param {string|null|undefined} entityId
 * @returns {string}
 */
export function getChartEntityLabel(chartEntities, entityId) {
    if (!entityId) {
        return '';
    }
    const match = chartEntities.find((entity) => entity.wikidataId === entityId);
    const label = match?.label;
    if (!isDisplayableEntityLabel(label, entityId)) {
        return '';
    }
    return String(label).trim();
}

/**
 * Visible user-message text only. Entity ids stay on the selected objects / chart
 * context so the backend can resolve by name + topology — they must not appear here.
 *
 * @param {{ fromEntityId: string, toEntityId: string, chartEntities: Array<{ wikidataId: string, label?: string }> }} params
 * @returns {string}
 */
export function buildTraceIndirectConnectionsPrompt({
    fromEntityId,
    toEntityId,
    chartEntities,
}) {
    const fromLabel = getChartEntityLabel(chartEntities, fromEntityId) || 'the first entity';
    const toLabel = getChartEntityLabel(chartEntities, toEntityId) || 'the second entity';

    return [
        `Trace the indirect connection between "${fromLabel}" and "${toLabel}".`,
        'These two entities are on this chart but are not directly linked by an arrow.',
        'Explain how they are connected through indirect relationships.',
        'Distinguish links shown on the chart from additional context found in the knowledge base.',
    ].join(' ');
}

/**
 * Suggested-prompt / composer phrasing that should open the in-chat entity picker
 * instead of being sent to the model immediately.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function isTraceIndirectConnectionsIntent(text) {
    const normalized = String(text || '').trim().toLowerCase().replace(/[.?!]+$/, '');
    return normalized === 'trace indirect connections'
        || normalized === 'trace the indirect connections';
}

/**
 * Toggle membership in a max-2 selection. Adding a third id keeps the latest pair.
 *
 * @param {string[]} selectedIds
 * @param {string} entityId
 * @returns {string[]}
 */
export function toggleTraceIndirectSelection(selectedIds = [], entityId) {
    if (!entityId) {
        return Array.isArray(selectedIds) ? selectedIds.filter(Boolean) : [];
    }
    const current = Array.isArray(selectedIds) ? selectedIds.filter(Boolean) : [];
    if (current.includes(entityId)) {
        return current.filter((id) => id !== entityId);
    }
    if (current.length >= 2) {
        return [current[1], entityId];
    }
    return [...current, entityId];
}

/**
 * Payload shape already used by `[[entity:…]]` chips / InlineComponentChip.
 *
 * @param {{ wikidataId?: string, label?: string, entityType?: string }} entity
 * @returns {{ entityRef: string, wikidataId: string, id: string, label: string, entityType?: string }|null}
 */
export function buildChartEntityChipPayload(entity) {
    if (!entity?.wikidataId) {
        return null;
    }
    if (!isDisplayableEntityLabel(entity.label, entity.wikidataId)) {
        return null;
    }
    const payload = {
        entityRef: entity.wikidataId,
        wikidataId: entity.wikidataId,
        id: entity.wikidataId,
        label: String(entity.label).trim(),
    };
    if (entity.entityType) {
        payload.entityType = entity.entityType;
    }
    return payload;
}
